import React, { useRef } from 'react';
import { getMediaUrl } from '../services/api';

interface Profile {
  id: string | number;
  name: string;
  role?: string;
  bio?: string;
  image?: {
    url: string;
  };
}

interface ProfileCarouselProps {
  profiles: Profile[];
  title?: string;
  quote?: string;
  className?: string;
}

const ProfileCarousel: React.FC<ProfileCarouselProps> = ({ profiles, title, quote, className = '' }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { scrollLeft, clientWidth } = scrollContainerRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollContainerRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  return (
    <section className={`overflow-hidden ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(title || quote) && (
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-8">
            {title && <h2 className="text-4xl font-serif font-bold text-brand-purple-900 text-center md:text-start">{title}</h2>}
            {quote && <p className="text-gray-500 max-w-md text-center md:text-end italic">{quote}</p>}
          </div>
        )}

        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        
        <div className="relative w-full flex items-center group/slider px-0 md:px-8">
          <button
            onClick={() => scroll('left')}
            className="absolute start-0 top-1/2 -translate-y-1/2 z-30 bg-white p-2 md:p-3 rounded-full shadow-lg text-brand-purple-900 hover:bg-brand-purple-50 transition-all opacity-100 md:opacity-0 md:group-hover/slider:opacity-100 hover:scale-110 pointer-events-auto"
          >
            <svg className="w-4 h-4 md:w-6 md:h-6 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute end-0 top-1/2 -translate-y-1/2 z-30 bg-white p-2 md:p-3 rounded-full shadow-lg text-brand-purple-900 hover:bg-brand-purple-50 transition-all opacity-100 md:opacity-0 md:group-hover/slider:opacity-100 hover:scale-110 pointer-events-auto"
          >
            <svg className="w-4 h-4 md:w-6 md:h-6 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>

          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto snap-x snap-mandatory gap-[7px] md:gap-12 pb-12 pt-4 px-4 -mx-4 hide-scrollbar scroll-smooth relative z-20 w-full"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {profiles.map((member: any, i) => {
              const name = member.name || member.attributes?.name || '';
              const role = member.role || member.attributes?.role || '';
              const bio = member.bio || member.attributes?.bio || '';
              const rawImg = member.image || member.attributes?.image || member.avatar || member.attributes?.avatar;
              const imgUrl = rawImg?.url 
                || rawImg?.data?.attributes?.url 
                || rawImg?.data?.url 
                || rawImg?.attributes?.url 
                || (typeof rawImg === 'string' ? rawImg : null);

              const imageSrc = imgUrl
                ? getMediaUrl(imgUrl)
                : `https://picsum.photos/seed/member${i + 20}/400/400`;

              return (
                <div key={member.id || member.documentId || i} className="flex-none w-[calc(50%-3.5px)] md:w-[calc(50%-12px)] lg:w-[calc(25%-36px)] snap-center text-center group relative flex flex-col items-center justify-center">
                  {/* Bio Popup */}
                  {bio && (
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] sm:w-[400px] p-8 bg-brand-purple-900 text-white text-sm md:text-base rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[9999] pointer-events-none transform scale-95 group-hover:scale-100">
                      <div className="relative z-10">
                        <h5 className="text-brand-yellow-400 font-bold mb-3">{name}</h5>
                        <p className="leading-relaxed font-light">{bio}</p>
                      </div>
                    </div>
                  )}

                  <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto mb-4 md:mb-8 cursor-pointer group/avatar">
                    <div className="absolute inset-0 bg-brand-purple-100 rounded-full scale-105 group-hover:bg-brand-yellow-400 transition-colors duration-500"></div>
                    <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto rounded-full overflow-hidden border-4 border-white shadow-lg">
                      <img
                        src={imageSrc}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                        alt={name}
                      />
                    </div>
                  </div>
                  <h4 className="text-base md:text-xl font-bold text-brand-purple-900 mb-0.5 truncate px-1">{name}</h4>
                  {role && <p className="text-brand-green-500 font-semibold text-[9px] md:text-sm uppercase tracking-widest truncate">{role}</p>}
                </div>
              );
            })}
          </div>

          {/* Arrows are moved to the wrapper above */}
        </div>
      </div>
    </section>
  );
};

export default ProfileCarousel;
